<template>
  <UppyUploaderWrapper
    :uppyId="uppyId"
    :metadata="metadata"
    :height="height"
    :document-type-id="documentTypeId"
    :upload-success="uploadSuccess"
    :upload-complete="uploadComplete"
    :contact-us="contactUs"
    @errorMessageUpdate="setErrorMessage"
    @uploadComplete="handleUploadComplete"
  >
    <div ref="root" class="file-upload" @toggleDashboard="toggleDashboard">
      <div v-if="uploadedFiles?.length <= 0" class="dashboard-wrapper">
        <div v-if="showToggle && !showInlineDashboard && !fileUploadComplete">
          <div class="row">
            <div class="col">{{ label }}</div>
            <div class="col end">
              <button @click="toggleDashboard" class="btn-primary btn btn-primary btn-primary">
                <span class="button-text">UPLOAD</span>
              </button>
            </div>
          </div>
        </div>
        <div
          v-show="showInlineDashboard === true"
          class="drag-drop-area"
          :id="uppyId"
          :name="name"
          :application="application"
          :applicant="applicant"
          :showToggle="showToggle"
          :documentTypeId="documentTypeId"
        >
          <div class="row">
            <div class="col">{{ label }}</div>
          </div>
        </div>
      </div>
      <div v-else v-show="uploadedFiles && uploadedFiles?.length > 0" class="uploaded-files">
        <div class="filetype">
          <div class="column-left">
            <font-awesome-icon class="icon complete" :icon="['fas', 'circle-check']" />
          </div>
          <div class="column-right">
            <p>&nbsp;{{ label }}</p>
          </div>
        </div>
        <div class="uploaded-file" v-for="(file, index) in uploadedFiles" :key="index">
          <ul>
            <li class="file">
              <div class="filename">
                <div class="column-left"></div>
                <div class="column-right">
                  <p>{{ file?.name }}</p>
                </div>
              </div>
            </li>
          </ul>
        </div>
      </div>
    </div>
  </UppyUploaderWrapper>
</template>
<script setup lang="ts">
import { ref, defineProps, computed, defineEmits, reactive } from "vue";
import { useStore } from "vuex";
import UppyUploaderWrapper from "@/components/shared/UppyUploaderWrapper.vue";
import { DocumentEntityTypes } from "@/models/enums";

const props = defineProps({
  application: { type: Object },
  applicant: { type: Object },
  dashboardOpen: { type: Boolean },
  documentTypeId: { type: Number },
  files: { type: Array },
  inline: { type: Boolean, default: true },
  label: { type: String, required: false },
  name: { type: String, required: false },
  showToggle: { type: Boolean },
  stipulationId: { type: Number, required: false },
  user: { type: Object },
  height: { type: Number, default: 400 },
  uploadSuccess: { type: Boolean, required: false },
  uploadComplete: { type: Boolean, required: false },
  entityTypeId: { type: Number },
});

const emit = defineEmits(["toggleDashboard", "uploadSuccess", "errorMessageUpdated"]);

const store = useStore();
const root = ref(null) as any;

const uppyId = computed<string>(() => `uppy_${props?.name}`);
const progressBarId = computed<string>(() => `uppy_${props?.name}_progressBar`);
const statusBarId = computed<string>(() => `uppy_${props?.name}_statusBar`);
const oidcUser = computed(() => store.getters["oidcStore/oidcUser"]);
const contactUs = computed(() => store.getters["contactUs"]);

const uploadedFiles = ref([] as any[]);
const fileUploadComplete = ref<undefined | boolean>(undefined);
const showInlineDashboard = ref<undefined | boolean>(undefined);
const errorMessageText = ref<string>("");

const metadata = reactive({
  entityId: props?.application?.id || props?.applicant?.applicationId,
  personId: props?.applicant?.id,
  entityTypeId: DocumentEntityTypes.Application,
  userId: oidcUser?.value?.sub,
  documentTypeId: props?.documentTypeId || 23,
  applicantVisibility: true,
  stipulationId: props?.stipulationId,
});

function setErrorMessage(event: any) {
  errorMessageText.value = event.errorMessage || "";
}

function handleUploadComplete(event: any) {
  uploadedFiles.value = event.successful;
  fileUploadComplete.value = true;
  showInlineDashboard.value = false;

  const file = event?.successful[0];
  if (file) {
    emit("uploadSuccess", { result: true });
  }
}

function toggleDashboard() {
  showInlineDashboard.value = !showInlineDashboard.value;
  const el = root.value as HTMLElement;
  el.scrollTop = 0;
  emit("toggleDashboard", true);
}
</script>
<style lang="scss" scoped>
// @import "../src/styles/uppyOverrides.css";

.file-upload {
  max-width: 100%;

  .dashboard-wrapper {
    .action {
      color: gray;
      font-size: 0.75em;
      text-decoration: none;
      cursor: pointer;
    }

    .col {
      &.end {
        display: flex;
        justify-content: flex-end;
      }
    }
  }

  .upload-btn {
    max-width: 90%;

    .btn {
      position: relative;
      background: var(--theme-color);

      &.loading {
        &::after {
          content: "";
          position: absolute;
          width: 16px;
          height: 16px;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          margin: auto;
          border: 4px solid transparent;
          border-top-color: #ffffff;
          border-radius: 50%;
          animation: button-loading-spinner 1s ease infinite;
        }

        .button-text {
          visibility: hidden;
          opacity: 0;
        }
      }
    }

    .cuscd-dark .btn.loading::after {
      border-top-color: #000100;
    }

    /* Small and Medium devices (phones, tablets, 992px and down) */
    @media (max-width: 992px) {
      .btn {
        font-size: 1em;
        max-width: calc(50% - 5px);
        min-width: 140px;

        .button-text {
          font-size: 1.1em;
        }
      }
    }

    @keyframes button-loading-spinner {
      from {
        transform: rotate(0turn);
      }

      to {
        transform: rotate(1turn);
      }
    }
  }

  .error {
    color: red;
  }

  &.active-drag {
    background-color: rgba(var(--theme-color-rgb), 0.3);
    transition: all 0.5s;
  }

  .uppy-Dashboard-input,
  input[type="file"] {
    opacity: 0;
    position: absolute;
    width: 1px;
    height: 1px;
    overflow: hidden;
    z-index: -1;
  }

  .input-label {
    cursor: pointer;
    font-weight: 700;
    margin-bottom: 0;
  }

  .uploaded-files {
    p {
      margin-bottom: 0;
    }

    .icon {
      &.complete {
        color: green;
      }
    }

    ul {
      list-style: none;
      margin: 0;
      padding: 0;

      li {
        color: green;
      }
    }

    .filetype,
    .filename {
      display: flex;
      font-weight: normal;
      font-size: 1rem;

      .column-left {
        width: 5%;
      }

      .column-right {
        width: 95%;
      }
    }
  }
}
</style>
